/**
 * The tech brief (#1728, Finish Line slice 4): what the tech's iPad will show
 * for a job, and what the field app will demand before the tech can finish.
 *
 * `GET /api/jobs/:id/tech-brief` (office_review.manage). These types mirror
 * `src/server/services/techBrief/types.ts`, the API contract; keep them in
 * step when that file changes. The query key sits under `["job", id]` so
 * every job invalidation re-reads it, like the Finish Line's.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface TechBriefDevice {
    assetId: string;
    label: string;
    action: string;
    size: string | null;
    make: string | null;
    model: string | null;
    serial: string | null;
    location: string | null;
    missing: string[];
}

export type PartState = "not_ordered" | "on_order" | "partial" | "received" | "staged" | "with_tech";

export interface TechBriefPart {
    name: string;
    quantity: number;
    state: PartState;
    words: string;
    poNumber: string | null;
}

export interface FinishRequirement {
    id: string;
    text: string;
    gate: string;
    officeCanFix: boolean;
}

export interface BriefGap {
    id: string;
    words: string;
}

export interface TechBrief {
    jobId: string;
    generatedAt: string;
    appointment: {
        real: boolean;
        served: boolean;
        status: string;
        scheduledStart: string | null;
        techName: string | null;
        reason: string | null;
    };
    site: {
        name: string | null;
        address: string | null;
        accessNotes: string | null;
        siteHours: string | null;
        gateCodeOnFile: boolean;
    };
    contact: { name: string | null; phone: string | null; role: string | null } | null;
    scope: { text: string | null; techNotes: string | null; officeToConfirm: boolean };
    devices: TechBriefDevice[];
    quotedDevicesNotLinked: number;
    parts: TechBriefPart[];
    waterAuthority: { name: string; platform: string | null } | null;
    checkIn: { phone: string | null; pin: string | null } | null;
    toFinish: FinishRequirement[];
    gaps: BriefGap[];
}

export const techBriefKey = (jobId: string | undefined) => ["job", jobId, "tech-brief"] as const;

export function useTechBrief(jobId: string | undefined, enabled: boolean) {
    return useQuery<TechBrief>({
        queryKey: techBriefKey(jobId),
        queryFn: async () => (await api.get<{ techBrief: TechBrief }>(`/api/jobs/${jobId}/tech-brief`)).techBrief,
        enabled: !!jobId && enabled,
        staleTime: 15_000,
        retry: false,
    });
}
